import { createSlice } from "@reduxjs/toolkit";
import {
  AccountFundAccessResponse,
  ContactResponse,
  UserRole,
  UserIFC,
  ProfileAuditResponse,
  Country,
} from "@myProfile/models";
import {
  fetchAccountFundAccessThunk,
  fetchContactInformationThunk,
  fetchUserRolesThunk,
  fetchUserIFCsThunk,
  fetchProfileAuditThunk,
  saveProfileThunk,
  fetchCountriesThunk,
  resetPasswordThunk,
  enrollMFAThunk,
  removeAccountFundAccessThunk,
} from "./thunks";

//users roles state interface
export interface UserRolesState {
  data: UserRole[] | null;
  loading: boolean;
  error: string | null;
}

// User IFCs (Investor Fund Contacts) state interface
// Used to determine profile menu visibility based on user's fund assignments
export interface UserIFCsState {
  data: UserIFC[] | null;
  loading: boolean;
  error: string | null;
}

// LPC-10991: Loading state for Auth0 password reset / MFA enroll requests.
// Used by Self Service pop-ups (Reset Password, Reset MFA) to disable the
// action button while the request is in flight, preventing duplicate sends.
export interface SelfServiceRequestState {
  loading: boolean;
  error: string | null;
  // Latched on pending, held through fulfilled (covers the dialog close animation),
  // cleared on rejected and on dialog re-open.
  submitted: boolean;
}

export interface RemovalSaveState {
  loading: boolean;
  error: string | null;
}

/** Per-row removal context from each SelfRemovalDialog confirm. */
export interface PendingRemovalRowContext {
  reasonIds: string[];
  reasonLabels: string[];
  additionalDetails: string;
}

/** One popup confirm: selected row ids share the same reason/details. */
export interface AddPendingRemovalBatchPayload {
  rowIds: string[];
  reasonIds: string[];
  reasonLabels: string[];
  additionalDetails: string;
}

export interface AccountFundAccessState {
  data: AccountFundAccessResponse | null;
  loading: boolean;
  error: string | null;
  resetPasswordDialogOpen: boolean;
  resetMFADialogOpen: boolean;
  resetMFAEmail: string | null;
  userRoles: UserRolesState;
  userIFCs: UserIFCsState;
  // LPC-10991: In-flight tracking for the two Auth0-backed self-service flows.
  resetPassword: SelfServiceRequestState;
  resetMFA: SelfServiceRequestState;
  isEditMode: boolean;
  pendingRemovals: string[];
  pendingRemovalContexts: Record<string, PendingRemovalRowContext>;
  removalSave: RemovalSaveState;
}

export interface CountriesState {
  data: Country[];
  loading: boolean;
  error: string | null;
}

export interface ProfileAuditState {
  data: ProfileAuditResponse | null;
  loading: boolean;
  error: string | null;
  fetched: boolean; // Track if API has been called (regardless of result)
}

export interface SaveProfileState {
  loading: boolean;
  error: string | null;
  lastResponse: { success: boolean; emailChangeRequested: boolean } | null;
}

export interface ContactInformationState {
  contactInformation: ContactResponse | null;
  contactLoading: boolean;
  contactError: string | null;
  countries: CountriesState;
  profileAudit: ProfileAuditState;
  saveProfile: SaveProfileState;
}

const initialState: AccountFundAccessState = {
  data: null,
  loading: false,
  error: null,
  resetPasswordDialogOpen: false,
  userRoles: {
    data: null,
    loading: false,
    error: null,
  },
  userIFCs: {
    data: null,
    loading: false,
    error: null,
  },
  resetMFADialogOpen: false,
  resetMFAEmail: null,
  // LPC-10991: Idle by default; toggled by thunk lifecycle below.
  resetPassword: {
    loading: false,
    error: null,
    submitted: false,
  },
  resetMFA: {
    loading: false,
    error: null,
    submitted: false,
  },
  isEditMode: false,
  pendingRemovals: [],
  pendingRemovalContexts: {},
  removalSave: {
    loading: false,
    error: null,
  },
};

const accountFundAccessSlice = createSlice({
  name: "accountFundAccess",
  initialState,
  reducers: {
    resetAccountFundAccessState: () => initialState,
    openResetPasswordDialog: (state) => {
      state.resetPasswordDialogOpen = true;
      state.resetPassword.submitted = false;
    },
    closeResetPasswordDialog: (state) => {
      state.resetPasswordDialogOpen = false;
    },
    openResetMFADialog: (state, { payload }: { payload?: string }) => {
      state.resetMFADialogOpen = true;
      state.resetMFAEmail = payload ?? null;
      state.resetMFA.submitted = false;
    },
    closeResetMFADialog: (state) => {
      state.resetMFADialogOpen = false;
      state.resetMFAEmail = null;
    },
    enterEditMode: (state) => {
      state.isEditMode = true;
    },
    // Clean exit: drop edit flag AND any pending removals so re-entering edit mode starts fresh.
    exitEditMode: (state) => {
      state.isEditMode = false;
      state.pendingRemovals = [];
      state.pendingRemovalContexts = {};
      state.removalSave.error = null;
    },
    addPendingRemovalBatch: (state, { payload }: { payload: AddPendingRemovalBatchPayload }) => {
      const next = new Set<string>(state.pendingRemovals);
      const context: PendingRemovalRowContext = {
        reasonIds: payload.reasonIds,
        reasonLabels: payload.reasonLabels,
        additionalDetails: payload.additionalDetails,
      };

      payload.rowIds.forEach((id) => {
        if (!id) return;
        next.add(id);
        state.pendingRemovalContexts[id] = context;
      });
      state.pendingRemovals = Array.from(next);
    },
    removePendingRemoval: (state, { payload }: { payload: string }) => {
      state.pendingRemovals = state.pendingRemovals.filter((id) => id !== payload);
      delete state.pendingRemovalContexts[payload];
    },
    clearPendingRemovals: (state) => {
      state.pendingRemovals = [];
      state.pendingRemovalContexts = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Account/Fund Access thunk handlers
      .addCase(fetchAccountFundAccessThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountFundAccessThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;

        if (state.pendingRemovals.length > 0) {
          const validIds = new Set<string>();

          action.payload.accounts.forEach((account) => {
            account.funds.forEach((fund) => {
              if (account.accountGlobalId && fund.fundGlobalId) {
                validIds.add(`${account.accountGlobalId}|${fund.fundGlobalId}`);
              }
            });
          });
          state.pendingRemovals = state.pendingRemovals.filter((id) => validIds.has(id));
          Object.keys(state.pendingRemovalContexts).forEach((id) => {
            if (!validIds.has(id)) {
              delete state.pendingRemovalContexts[id];
            }
          });
        }
      })
      .addCase(fetchAccountFundAccessThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // User Roles thunk handlers
      .addCase(fetchUserRolesThunk.pending, (state) => {
        state.userRoles.loading = true;
        state.userRoles.error = null;
      })
      .addCase(fetchUserRolesThunk.fulfilled, (state, action) => {
        state.userRoles.loading = false;
        state.userRoles.data = action.payload;
        state.userRoles.error = null;
      })
      .addCase(fetchUserRolesThunk.rejected, (state, action) => {
        state.userRoles.loading = false;
        state.userRoles.error = action.payload as string;
      })
      // User IFCs thunk handlers
      .addCase(fetchUserIFCsThunk.pending, (state) => {
        state.userIFCs.loading = true;
        state.userIFCs.error = null;
      })
      .addCase(fetchUserIFCsThunk.fulfilled, (state, action) => {
        state.userIFCs.loading = false;
        state.userIFCs.data = action.payload;
        state.userIFCs.error = null;
      })
      .addCase(fetchUserIFCsThunk.rejected, (state, action) => {
        state.userIFCs.loading = false;
        state.userIFCs.error = action.payload as string;
      })
      // LPC-10991: Reset Password (Auth0) — mirrors InvestorRequest pattern.
      // Disables the SEND RESET LINK button while in flight to prevent
      // duplicate Auth0 /dbconnections/change_password requests.
      .addCase(resetPasswordThunk.pending, (state) => {
        state.resetPassword.loading = true;
        state.resetPassword.error = null;
        state.resetPassword.submitted = true;
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.resetPassword.loading = false;
        state.resetPassword.error = null;
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.resetPassword.loading = false;
        state.resetPassword.error = (action.payload as string) ?? null;
        state.resetPassword.submitted = false;
      })
      // LPC-10991: Reset MFA (Entitlements) — same single-flight guard.
      .addCase(enrollMFAThunk.pending, (state) => {
        state.resetMFA.loading = true;
        state.resetMFA.error = null;
        state.resetMFA.submitted = true;
      })
      .addCase(enrollMFAThunk.fulfilled, (state) => {
        state.resetMFA.loading = false;
        state.resetMFA.error = null;
      })
      .addCase(enrollMFAThunk.rejected, (state, action) => {
        state.resetMFA.loading = false;
        state.resetMFA.error = (action.payload as string) ?? null;
        state.resetMFA.submitted = false;
      })
      .addCase(removeAccountFundAccessThunk.pending, (state) => {
        state.removalSave.loading = true;
        state.removalSave.error = null;
      })
      .addCase(removeAccountFundAccessThunk.fulfilled, (state) => {
        state.removalSave.loading = false;
        state.removalSave.error = null;
        state.pendingRemovals = [];
        state.pendingRemovalContexts = {};
        state.isEditMode = false;
      })
      .addCase(removeAccountFundAccessThunk.rejected, (state, action) => {
        state.removalSave.loading = false;
        state.removalSave.error = (action.payload as string) || "Failed to remove access. Please try again.";
        // Intentionally keep pendingRemovals + isEditMode so the user can retry from the same view.
      });
  },
});

export const {
  resetAccountFundAccessState,
  openResetPasswordDialog,
  closeResetPasswordDialog,
  openResetMFADialog,
  closeResetMFADialog,
  enterEditMode,
  exitEditMode,
  addPendingRemovalBatch,
  removePendingRemoval,
  clearPendingRemovals,
} = accountFundAccessSlice.actions;
export default accountFundAccessSlice.reducer;

// Contact Information Slice
const contactInformationInitialState: ContactInformationState = {
  contactInformation: null,
  contactLoading: false,
  contactError: null,
  countries: {
    data: [],
    loading: false,
    error: null,
  },
  profileAudit: {
    data: null,
    loading: false,
    error: null,
    fetched: false,
  },
  saveProfile: {
    loading: false,
    error: null,
    lastResponse: null,
  },
};

const contactInformationSlice = createSlice({
  name: "contactInformation",
  initialState: contactInformationInitialState,
  reducers: {
    resetContactInformationState: () => contactInformationInitialState,
    clearSaveProfileResponse: (state) => {
      state.saveProfile.lastResponse = null;
    },
    resetProfileAuditFetched: (state) => {
      state.profileAudit.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Contact Information thunk handlers
      .addCase(fetchContactInformationThunk.pending, (state) => {
        state.contactLoading = true;
        state.contactError = null;
      })
      .addCase(fetchContactInformationThunk.fulfilled, (state, action) => {
        state.contactLoading = false;
        state.contactInformation = action.payload;
        state.contactError = null;
      })
      .addCase(fetchContactInformationThunk.rejected, (state, action) => {
        state.contactLoading = false;
        state.contactError = action.payload as string;
      })
      // Countries thunk handlers
      .addCase(fetchCountriesThunk.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
      })
      .addCase(fetchCountriesThunk.fulfilled, (state, action) => {
        state.countries.loading = false;
        state.countries.data = action.payload;
        state.countries.error = null;
      })
      .addCase(fetchCountriesThunk.rejected, (state, action) => {
        state.countries.loading = false;
        state.countries.error = (action.payload as string) || "Failed to load countries";
      })
      // Profile Audit thunk handlers
      .addCase(fetchProfileAuditThunk.pending, (state) => {
        state.profileAudit.loading = true;
        state.profileAudit.error = null;
      })
      .addCase(fetchProfileAuditThunk.fulfilled, (state, action) => {
        state.profileAudit.loading = false;
        state.profileAudit.data = action.payload;
        state.profileAudit.error = null;
        state.profileAudit.fetched = true;
      })
      .addCase(fetchProfileAuditThunk.rejected, (state, action) => {
        state.profileAudit.loading = false;
        state.profileAudit.error = action.payload as string;
        state.profileAudit.fetched = true;
      })
      // Save Profile thunk handlers
      .addCase(saveProfileThunk.pending, (state) => {
        state.saveProfile.loading = true;
        state.saveProfile.error = null;
      })
      .addCase(saveProfileThunk.fulfilled, (state, action) => {
        state.saveProfile.loading = false;
        state.saveProfile.lastResponse = {
          success: action.payload.success,
          emailChangeRequested: action.payload.emailChangeRequested,
        };
        state.saveProfile.error = null;
      })
      .addCase(saveProfileThunk.rejected, (state, action) => {
        state.saveProfile.loading = false;
        state.saveProfile.error = action.payload as string;
      });
  },
});

export const { resetContactInformationState, clearSaveProfileResponse, resetProfileAuditFetched } =
  contactInformationSlice.actions;
export const contactInformationReducer = contactInformationSlice.reducer;
